import type { SearchableField } from "./schema";
import { SEARCH_SORTS, type FilterSelection, type SearchSort } from "./parser";

export interface BrowserState {
  query: string;
  fields: SearchableField[];
  filters: FilterSelection[];
  sort: SearchSort;
}

export const DEFAULT_BROWSER_STATE: BrowserState = {
  query: "",
  fields: [],
  filters: [],
  sort: "source_order",
};

function isSearchSort(value: string | null): value is SearchSort {
  return value !== null && (SEARCH_SORTS as readonly string[]).includes(value);
}

export function parseBrowserState(
  searchParams: URLSearchParams,
  defaults: BrowserState = DEFAULT_BROWSER_STATE
): BrowserState {
  const query = searchParams.get("q") ?? "";
  const fields =
    (searchParams.get("fields")?.split(",").filter(Boolean) as SearchableField[]) ?? [];
  const rawSort = searchParams.get("sort");
  const sort = isSearchSort(rawSort) ? rawSort : defaults.sort;

  const filters: FilterSelection[] = [];
  const filterParam = searchParams.get("filters");
  if (filterParam) {
    try {
      const parsed = JSON.parse(filterParam);
      if (Array.isArray(parsed)) {
        filters.push(...parsed);
      }
    } catch {
      // ignore malformed filter state
    }
  }

  return { query, fields, filters, sort };
}

export function serializeBrowserState(
  state: BrowserState,
  defaults: BrowserState = DEFAULT_BROWSER_STATE
): string {
  const params = new URLSearchParams();
  if (state.query) params.set("q", state.query);
  if (state.fields.length > 0) params.set("fields", state.fields.join(","));
  if (state.sort !== defaults.sort) params.set("sort", state.sort);
  if (state.filters.length > 0) params.set("filters", JSON.stringify(state.filters));
  return params.toString();
}

export function toggleField(fields: SearchableField[], field: SearchableField): SearchableField[] {
  return fields.includes(field) ? fields.filter((f) => f !== field) : [...fields, field];
}

export function updateFilter(
  filters: FilterSelection[],
  field: FilterSelection["field"],
  updater: (selection: FilterSelection) => FilterSelection
): FilterSelection[] {
  const existing = filters.find((f) => f.field === field);
  if (!existing) {
    return [...filters, updater({ field, values: [], operator: "or" })];
  }
  return filters.map((f) => (f.field === field ? updater(f) : f));
}

export function countActiveFilters(state: BrowserState): number {
  return state.filters.reduce((sum, f) => sum + f.values.length, 0);
}
